/** @jsx jsx */
import { useContext } from "react"
import { jsx, useThemeUI } from "theme-ui"
import PropTypes from "prop-types"

import ModeContext from "../context/mode-context"
import Link from "./link"
import Image from "./image"
import { COLUMN_RATIOS, twoColumnLayout } from "./contentpage-two-column-layout"

const ContentPageTwoColumn = ({
  image,
  imageAlt,
  heading,
  headlineName,
  children,
  link,
  linkText,
  linkTarget,
  disableHyphens,
  mode,
  reverse,
  ratio,
  stackOrder,
  currentLanguage,
  imagePaddingTop,
  headingTop,
  inMiddle,
  isBlogChild,
  date,
  buttonVariant,
}) => {
  const { mode: globalMode } = useContext(ModeContext)
  const { rawColors: colors } = useThemeUI().theme
  let localeDate = new Date(date).toLocaleDateString("de-DE")

  let blogDate

  const convertToUSDate = (germanDate) => {
    const [day, month, year] = germanDate.split(".")
    const usDate = `${month}/${day}/${year}`
    return usDate
  }
  if (process.env.GATSBY_CHANGE_DATE_FORMAT_US === "true") {
    localeDate = convertToUSDate(localeDate)
  }

  const modeKey = mode ? mode : globalMode
  const modeColor =
    colors.modes[modeKey]?.primary ?? colors.modes["group"].primary

  const isSolid = buttonVariant === "solid"

  const layout = twoColumnLayout({ ratio, reverse, stackOrder, inMiddle })

  const buttonSx = isSolid
    ? {
        variant: "styles.solidButton",
        px: 4,
        py: 2,
        bg: modeColor,
        borderColor: modeColor,
        color: "#fff",
        "&:hover": {
          bg: modeColor,
          color: "#fff",
        },
      }
    : {
        variant: "styles.ghostButton",
        px: 4,
        py: 2,
        color: modeColor,
        borderColor: modeColor,
        "&:hover": {
          bg: modeColor,
          color: "#fff",
        },
      }

  if (isBlogChild) {
    blogDate = (
      <span
        sx={{
          display: date ? "block" : "none",
        }}
      >
        {localeDate}
      </span>
    )
  }

  let hyphensStyling = {}
  if (disableHyphens) {
    hyphensStyling = {
      hyphens: "none",
      wordWrap: "normal",
    }
  }
  return (
    <div
      sx={{
        variant: "spacingContainer",
      }}
    >
      {headingTop && (
        <h2
          sx={{
            pb: 5,
          }}
        >
          {" "}
          {headingTop}{" "}
        </h2>
      )}
      <div sx={layout.container}>
        <div
          sx={{
            ...layout.imageColumn,
            pt: imagePaddingTop ? 5 : 0,
          }}
        >
          <Image
            image={
              typeof image === "object"
                ? {
                    ...image,
                    sizes: layout.columns.gatsbySizes,
                  }
                : image
            }
            sizes={layout.columns.imageSizes}
            alt={imageAlt}
            style={layout.imageStyle}
            objectFit={layout.imageObjectFit}
          />
        </div>
        <div
          sx={{
            ...layout.textColumn,
            py: 2,
          }}
        >
          {blogDate}
          <h2
            id={headlineName}
            sx={{
              p: 0,
              pb: "0 !important",
              m: 0,
              ...hyphensStyling,
            }}
          >
            {heading}
          </h2>
          {children}
          {linkText && (
            <div
              sx={{
                paddingTop: 4,
              }}
            >
              {mode === "gutesiegel" ? (
                <Link
                  to={link}
                  target={linkTarget}
                  language={currentLanguage}
                  role="button"
                  tabIndex={0}
                  sx={{
                    color: "text",
                    textDecoration: "none",
                    float: "right",
                    "&:after": {
                      content: "''",
                      width: "100%",
                      height: 1,
                      backgroundColor: "#5a5d5f",
                      display: "block",
                      transition: "200ms ease-out",
                    },
                    "&:hover": {
                      "&:after": {
                        width: "0%",
                      },
                    },
                  }}
                >
                  {linkText}
                </Link>
              ) : (
                <Link
                  to={link}
                  target={linkTarget}
                  language={currentLanguage}
                  sx={{
                    ...buttonSx,
                  }}
                >
                  {linkText}
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

ContentPageTwoColumn.propTypes = {
  image: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
  imageAlt: PropTypes.string,
  heading: PropTypes.string.isRequired,
  headlineName: PropTypes.string,
  children: PropTypes.node,
  link: PropTypes.string,
  linkText: PropTypes.string,
  linkTarget: PropTypes.string,
  mode: PropTypes.oneOf([
    "",
    "pharma",
    "food",
    "environment",
    "career",
    "construction",
    "medical",
    "lifeScience",
    "agroChemical",
    "sustainabilityServices",
    "none",
  ]),
  reverse: PropTypes.bool,
  ratio: PropTypes.oneOf(["", ...Object.keys(COLUMN_RATIOS)]),
  stackOrder: PropTypes.oneOf(["", "image-first", "text-first"]),
  disableHyphens: PropTypes.bool,
  inMiddle: PropTypes.bool,
  headingTop: PropTypes.string,
  buttonVariant: PropTypes.oneOf(["ghost", "solid"]),
}

ContentPageTwoColumn.defaultProps = {
  mode: "",
  reverse: false,
  disableHyphens: false,
}

export default ContentPageTwoColumn
